import { ALL_INVOICE_PAGE_SIZES } from "@/constants/invoice";
import { workspaceSectionPath } from "@/constants/routes";
import { EmptyState, LoadingState } from "@/components/shared/states";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/field";
import { InvoiceDocument } from "@/features/invoices/invoice-document";
import { useInvoice } from "@/features/invoices/api";
import { useOrder } from "@/features/orders/api";
import { useWorkspaces, useWorkspaceAccess } from "@/features/workspaces/api";
import { buildPrintPageRule, resolveInvoicePageSize, type InvoicePageSizeId } from "@bizy/core";
import { usePreferences } from "@/providers/preferences-provider";
import { ArrowLeft, Printer } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

/**
 * `/w/:slug/invoices/:invoiceId/print` — print preview. The `@page` rule is
 * injected from the selected page size so the browser print dialog opens with the
 * right paper dimensions (PRD §3.2.4).
 */
export const InvoicePrintPage = () => {
  const { workspaceSlug, invoiceId } = useParams<{ workspaceSlug: string; invoiceId: string }>();
  const { locale } = usePreferences();
  const workspacesQuery = useWorkspaces();
  const workspace = workspacesQuery.data?.items.find((item) => item.slug === workspaceSlug);
  const accessQuery = useWorkspaceAccess(workspace?.id);
  const invoiceQuery = useInvoice(workspace?.id, invoiceId);
  const orderQuery = useOrder(workspace?.id, invoiceQuery.data?.order_id);
  const [pageSize, setPageSize] = useState<InvoicePageSizeId | null>(null);

  const activePageSize = pageSize ?? invoiceQuery.data?.page_size ?? "a4";
  const pageRule = useMemo(
    () => buildPrintPageRule(resolveInvoicePageSize(activePageSize)),
    [activePageSize],
  );

  useEffect(() => {
    const element = document.createElement("style");
    element.setAttribute("data-bizy-print", activePageSize);
    element.textContent = pageRule;
    document.head.appendChild(element);
    return () => element.remove();
  }, [pageRule, activePageSize]);

  if (workspacesQuery.isLoading || accessQuery.isLoading || invoiceQuery.isLoading) {
    return <LoadingState label="Preparing the invoice…" />;
  }

  if (!workspace || !accessQuery.data || !invoiceQuery.data) {
    return (
      <div className="p-6">
        <EmptyState
          title="Invoice unavailable"
          description="The invoice may have been voided, or you do not have access to this workspace."
          action={
            <Button asChild>
              <Link to={workspaceSlug ? workspaceSectionPath(workspaceSlug, "orders") : "/dashboard"}>
                Back to orders
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 py-6">
      <div className="print-hide mx-auto mb-4 flex w-full max-w-4xl flex-wrap items-center justify-between gap-3 px-4">
        <Button asChild variant="outline" size="sm">
          <Link to={workspaceSectionPath(workspace.slug, "orders")}>
            <ArrowLeft className="size-4" aria-hidden />
            Back
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Select
            aria-label="Page size"
            value={activePageSize}
            onChange={(event) => setPageSize(event.target.value as InvoicePageSizeId)}
            className="w-44"
          >
            {ALL_INVOICE_PAGE_SIZES.map((size) => (
              <option key={size.id} value={size.id}>
                {size.label}
              </option>
            ))}
          </Select>
          <Button size="sm" onClick={() => window.print()}>
            <Printer className="size-4" aria-hidden />
            Print
          </Button>
        </div>
      </div>

      <div className="mx-auto w-full max-w-4xl px-4">
        <InvoiceDocument
          invoice={{ ...invoiceQuery.data, page_size: activePageSize }}
          order={orderQuery.data ?? invoiceQuery.data.order}
          items={orderQuery.data?.items ?? []}
          workspace={accessQuery.data.workspace}
          settings={accessQuery.data.settings}
          locale={locale}
        />
      </div>
    </div>
  );
};
